/** Date-only values are calendar dates, never instants in the user's time zone. */
export function isoDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match || match[1] === "0000") return ""
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? value : ""
}

export function parseDateInput(value: string): string {
  const text = value.trim()
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(text)
  return isoDate(match ? `${match[3]}-${match[2]}-${match[1]}` : text)
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—"
  if (!isoDate(value)) return value
  return `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}`
}

const formatters = new Map<string, Intl.DateTimeFormat>()

/** Keep time zones and time precision, but give every full date one numeric order. */
export function dateFormatter(locale?: Intl.LocalesArgument, options: Intl.DateTimeFormatOptions = {}) {
  const key = JSON.stringify([locale, options])
  const cached = formatters.get(key)
  if (cached) return cached
  const fullDate = options.dateStyle || options.day || Object.keys(options).length === 0 ||
    (options.timeZone && Object.keys(options).length === 1)
  const { dateStyle, timeStyle, ...rest } = options
  const formatter = fullDate ? new Intl.DateTimeFormat("en-GB", {
    ...rest,
    ...(timeStyle ? { hour: "2-digit", minute: "2-digit", ...(timeStyle !== "short" ? { second: "2-digit" } : {}) } as const : {}),
    calendar: "gregory", numberingSystem: "latn", day: "2-digit", month: "2-digit", year: "numeric",
  }) : new Intl.DateTimeFormat(locale, options)
  if (formatters.size >= 64) formatters.delete(formatters.keys().next().value!)
  formatters.set(key, formatter)
  return formatter
}

export function formatDateTime(value: string | number | Date, options: Intl.DateTimeFormatOptions = { dateStyle: "short", timeStyle: "short" }) {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isFinite(date.getTime()) ? dateFormatter(undefined, options).format(date) : "—"
}
